/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { useToast } from '../ui/use-toast';
import { Loader2, Plus, Edit, Trash2, Tag } from 'lucide-react';
import { marketplaceActions } from '../../actions/marketplace.action';
import {
  MarketplaceAsset,
  MarketplaceCategory,
  ASSET_STATUSES,
  ASSET_CURRENCIES,
} from '../../types/marketplace/marketplace';

interface FormState {
  name: string;
  issuer: string;
  category: string;
  status: string;
  currency: string;
  region: string;
  term: string;
  targetYield: number;
  minInvestment: number;
  aum: number;
  summary: string;
  featured: boolean;
  sparkline: string; // comma-separated numbers
  performance: string; // "month,value" per line
  documents: string; // one per line
  risks: string; // one per line
}

const emptyForm = (): FormState => ({
  name: '',
  issuer: '',
  category: 'Treasuries',
  status: 'Live',
  currency: 'USD',
  region: '',
  term: '',
  targetYield: 0,
  minInvestment: 0,
  aum: 0,
  summary: '',
  featured: false,
  sparkline: '',
  performance: '',
  documents: '',
  risks: '',
});

const MarketplaceManagement = () => {
  const [assets, setAssets] = useState<MarketplaceAsset[]>([]);
  const [categories, setCategories] = useState<MarketplaceCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<MarketplaceAsset | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [formLoading, setFormLoading] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [showCats, setShowCats] = useState(false);
  const [newCat, setNewCat] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    fetchAssets();
    fetchCategories();
  }, []);

  const fetchAssets = async () => {
    try {
      setLoading(true);
      const res = await marketplaceActions.getAssets();
      setAssets(res.data || []);
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to fetch assets', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await marketplaceActions.getCategories();
      setCategories(res.data || []);
    } catch (e) {
      /* non-fatal */
    }
  };

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm());
    setShowForm(true);
  };

  const openEdit = (a: MarketplaceAsset) => {
    setEditing(a);
    setForm({
      name: a.name,
      issuer: a.issuer,
      category: a.category,
      status: a.status,
      currency: a.currency,
      region: a.region || '',
      term: a.term || '',
      targetYield: a.targetYield,
      minInvestment: a.minInvestment,
      aum: a.aum,
      summary: a.summary || '',
      featured: !!a.featured,
      sparkline: (a.sparkline || []).join(', '),
      performance: (a.performance || []).map((p) => `${p.month},${p.value}`).join('\n'),
      documents: (a.documents || []).join('\n'),
      risks: (a.risks || []).join('\n'),
    });
    setShowForm(true);
  };

  const submitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setFormLoading(true);
      const payload: any = {
        name: form.name,
        issuer: form.issuer,
        category: form.category,
        status: form.status,
        currency: form.currency,
        region: form.region,
        term: form.term,
        targetYield: Number(form.targetYield),
        minInvestment: Number(form.minInvestment),
        aum: Number(form.aum),
        summary: form.summary,
        featured: form.featured,
        sparkline: form.sparkline
          .split(',')
          .map((n) => Number(n.trim()))
          .filter((n) => !isNaN(n)),
        performance: form.performance
          .split('\n')
          .map((line) => line.trim())
          .filter(Boolean)
          .map((line) => {
            const [month, value] = line.split(',');
            return { month: (month || '').trim(), value: Number((value || '').trim()) || 0 };
          }),
        documents: form.documents.split('\n').map((d) => d.trim()).filter(Boolean),
        risks: form.risks.split('\n').map((r) => r.trim()).filter(Boolean),
      };
      if (editing) {
        await marketplaceActions.updateAsset(editing._id, payload);
        toast({ title: 'Success', description: 'Asset updated' });
      } else {
        await marketplaceActions.addAsset(payload);
        toast({ title: 'Success', description: 'Asset created' });
      }
      setShowForm(false);
      fetchAssets();
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to save asset', variant: 'destructive' });
    } finally {
      setFormLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      setDeleteLoading(true);
      await marketplaceActions.deleteAsset(deleteId);
      toast({ title: 'Success', description: 'Asset deleted' });
      setDeleteId(null);
      fetchAssets();
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' });
    } finally {
      setDeleteLoading(false);
    }
  };

  const addCategory = async () => {
    if (!newCat.trim()) return;
    try {
      await marketplaceActions.addCategory({ label: newCat.trim(), value: newCat.trim() });
      setNewCat('');
      fetchCategories();
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to add category', variant: 'destructive' });
    }
  };

  const removeCategory = async (id: string) => {
    try {
      await marketplaceActions.deleteCategory(id);
      fetchCategories();
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to delete category', variant: 'destructive' });
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return assets.filter((a) => {
      if (catFilter !== 'all' && a.category !== catFilter) return false;
      if (!q) return true;
      return a.name.toLowerCase().includes(q) || a.issuer.toLowerCase().includes(q);
    });
  }, [assets, search, catFilter]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Marketplace</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowCats(true)}>
            <Tag className="h-4 w-4 mr-2" />
            Manage Categories
          </Button>
          <Button onClick={openAdd}>
            <Plus className="h-4 w-4 mr-2" />
            Add Asset
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-2 md:flex-row md:items-center">
        <Input
          placeholder="Search name or issuer"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="md:max-w-sm"
        />
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger className="md:w-56">
            <SelectValue placeholder="All categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {categories.map((c) => (
              <SelectItem key={c._id} value={c.value}>
                {c.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Assets ({filtered.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Issuer</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Yield</TableHead>
                <TableHead>AUM</TableHead>
                <TableHead>Featured</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((a) => (
                <TableRow key={a._id}>
                  <TableCell className="font-medium">{a.name}</TableCell>
                  <TableCell>{a.issuer}</TableCell>
                  <TableCell>{a.category}</TableCell>
                  <TableCell>{a.status}</TableCell>
                  <TableCell>{a.targetYield ? `${a.targetYield}%` : '—'}</TableCell>
                  <TableCell>
                    {a.currency} {a.aum}
                  </TableCell>
                  <TableCell>{a.featured ? 'Yes' : '—'}</TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      <Button variant="outline" size="sm" onClick={() => openEdit(a)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setDeleteId(a._id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-muted-foreground py-6">
                    No assets.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Asset' : 'Add Asset'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitForm} className="space-y-3">
            <p className="text-sm font-medium text-muted-foreground">Outer card</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              </div>
              <div>
                <Label>Issuer</Label>
                <Input value={form.issuer} onChange={(e) => setForm({ ...form, issuer: e.target.value })} required />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (
                      <SelectItem key={c._id} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ASSET_STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Currency</Label>
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ASSET_CURRENCIES.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Target yield (%)</Label>
                <Input type="number" step="0.01" value={form.targetYield} onChange={(e) => setForm({ ...form, targetYield: Number(e.target.value) })} />
              </div>
              <div>
                <Label>Min investment</Label>
                <Input type="number" value={form.minInvestment} onChange={(e) => setForm({ ...form, minInvestment: Number(e.target.value) })} />
              </div>
              <div>
                <Label>AUM</Label>
                <Input type="number" value={form.aum} onChange={(e) => setForm({ ...form, aum: Number(e.target.value) })} />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Term</Label>
                <Input value={form.term} onChange={(e) => setForm({ ...form, term: e.target.value })} placeholder="Open-ended" />
              </div>
              <div>
                <Label>Region</Label>
                <Input value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} placeholder="Global" />
              </div>
              <div>
                <Label>Featured</Label>
                <Select value={form.featured ? 'yes' : 'no'} onValueChange={(v) => setForm({ ...form, featured: v === 'yes' })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no">No</SelectItem>
                    <SelectItem value="yes">Yes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Sparkline (comma-separated numbers)</Label>
              <Input value={form.sparkline} onChange={(e) => setForm({ ...form, sparkline: e.target.value })} placeholder="100, 102, 101, 104, 106" />
            </div>

            <p className="text-sm font-medium text-muted-foreground pt-2">Inner detail</p>
            <div>
              <Label>Summary</Label>
              <Textarea value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} />
            </div>
            <div>
              <Label>{'Performance series (one "Month,Value" per line)'}</Label>
              <Textarea value={form.performance} onChange={(e) => setForm({ ...form, performance: e.target.value })} placeholder={'Jan,100\nFeb,102\nMar,105'} />
            </div>
            <div>
              <Label>Documents (one URL per line)</Label>
              <Textarea value={form.documents} onChange={(e) => setForm({ ...form, documents: e.target.value })} />
            </div>
            <div>
              <Label>Risks (one per line)</Label>
              <Textarea value={form.risks} onChange={(e) => setForm({ ...form, risks: e.target.value })} />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button type="submit" disabled={formLoading}>
                {formLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
          </DialogHeader>
          <div className="py-4">Delete this asset?</div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDeleteId(null)} disabled={deleteLoading}>No</Button>
            <Button variant="destructive" onClick={confirmDelete} disabled={deleteLoading}>
              {deleteLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Yes'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Category manager */}
      <Dialog open={showCats} onOpenChange={setShowCats}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Marketplace Categories</DialogTitle>
          </DialogHeader>
          <div className="flex gap-2">
            <Input placeholder="New category (e.g. Infrastructure)" value={newCat} onChange={(e) => setNewCat(e.target.value)} />
            <Button onClick={addCategory} disabled={!newCat.trim()}>Add</Button>
          </div>
          <div className="mt-3 space-y-2">
            {categories.map((c) => (
              <div key={c._id} className="flex items-center justify-between rounded border px-3 py-2">
                <span>{c.label}</span>
                <Button variant="outline" size="sm" onClick={() => removeCategory(c._id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            {categories.length === 0 && (
              <p className="text-center text-sm text-muted-foreground py-4">No categories.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MarketplaceManagement;
